import React from 'react'
import gql from 'graphql-tag'
import { Query, QueryResult } from 'react-apollo'
import { ILeaseCounters } from '@api'
import { H4, Card } from '@blueprintjs/core'
import { compact } from 'lodash'
import { Chart, Flex, NonIdealState } from '@ui'
import { ResponsiveContainer, TooltipProps } from 'recharts'
import { SubCardsWrapper, SubCard } from './CounterSubCard'
import { formatNumber } from './utils'
import LoadingState from './LoadingState'
import Bar from '@ui/charts/Bar'
// import moment from 'moment'
import css from './dashboard.module.scss'
import { DateUtil } from '@utils'

const query = gql`
  query getLeaseData($cities: [String], $dateFilters: [ContractDateFiltersInput]) {
    viewer {
      company {
        contracts(
          type: Lease
          cities: $cities
          sortBy: title
          order: ASC
          dateFilters: $dateFilters
        ) {
          counters {
            leases
            buildings
            floors
          }
          edges {
            node {
              title
              metadata
            }
          }
        }
      }
    }
  }
`

interface ILease {
  title: string
  metadata: string
}

interface IPayload {
  viewer: {
    company: {
      contracts: {
        counters: ILeaseCounters
        edges: [{ node: ILease }]
      }
    }
  }
}

interface LeasesCardProps {
  city: string | null
}

interface IVariables {
  cities: string[] | null
}

const dateFilters = [{ key: 'endDate', value: { fromDate: DateUtil().format('yyyy-MM-dd') } }]
export default function LeasesCard({ city }: LeasesCardProps) {
  return (
    <Card style={{ width: '100%' }}>
      <H4>Active Leases</H4>
      <Query
        notifyOnNetworkStatusChange
        query={query}
        variables={{ cities: compact([city]), dateFilters: dateFilters }}
      >
        {({ loading, data }: QueryResult<IPayload>) => {
          if (loading || !data || !data.viewer) {
            return <LoadingState />
          }

          const {
            counters: { leases: leaseCounter, buildings: builldingCounter, floors: floorCounter },
            edges,
          } = data.viewer.company.contracts

          const leases = edges
            .map(({ node: { title, metadata } }) => {
              return {
                title,
                sqft: Number.parseInt(JSON.parse(metadata).leaseArea) || 0,
              }
            })
            .sort((l1, l2) => l2.sqft - l1.sqft)

          const leaseFooters = [
            { title: 'Leases', value: leaseCounter },
            { title: 'Buildings', value: builldingCounter },
            { title: 'Floors', value: floorCounter },
          ]

          return (
            <>
              {leases.length ? (
                <ResponsiveContainer height={200} width="100%">
                  <Bar
                    data={leases}
                    xdata={{}}
                    ydata={{ key: 'sqft', label: 'SQFT' }}
                    layout="horizontal"
                    height={200}
                    width={200}
                    tooltip={(props) => <CustomTooltip {...props} />}
                    margin={{ top: 5, bottom: 5 }}
                  />
                </ResponsiveContainer>
              ) : (
                <Flex align="center" justify="center" style={{ height: 200 }}>
                  <NonIdealState
                    iconProps={{
                      name: 'file-contract',
                      color: '#ccc',
                      background: 'rgba(0,0,0,0.05)',
                    }}
                    message="To add lease data, go to Manage > Leases."
                  />
                </Flex>
              )}
              <SubCardsWrapper>
                {leaseFooters.map(({ title, value }, i) => (
                  <SubCard key={`leases-subcard-${i}`} {...{ title, value: formatNumber(value) }} />
                ))}
              </SubCardsWrapper>
            </>
          )
        }}
      </Query>
    </Card>
  )
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload) {
    return null
  }

  const { title, sqft } = payload[0].payload
  return (
    <Chart.Tooltip>
      <Flex align="center">{title}</Flex>
      <div className={css.count}>{formatNumber(sqft)} sqft</div>
    </Chart.Tooltip>
  )
}
